/**
 * CsvDownloadConfigModal — config UI for the `csv_download` leg-node type.
 *
 * Three views in one modal frame:
 *   1. 'config'  — fields, filters, date range, total desired, unique toggle
 *   2. 'summary' — read-only breakdown of what would actually export
 *   3. 'history' — prior downloads from THIS instance, with re-download links
 *
 * Flow:
 *   user edits config →
 *   clicks Download → 'summary' view (computes via /summary endpoint) →
 *   clicks Finalize → POST /finalize → modal switches to 'history' view
 *   and polls /files/:fileId every 2s until status leaves 'queued'/'processing'.
 *
 * UI patterns intentionally mirrored from the rest of the app:
 *   - Top-right counter block (mirrors vertebrae stats — filesCount / rowsTotal
 *     instead of Pass/Failed). Skeleton shimmer while loading.
 *   - Info "(i)" icon on every section heading (FieldTooltip — same as
 *     LegNodeConfigModal / VertebraConfigModal).
 *   - Filter-rule builder is the SAME shape as the vertebra filter_rule
 *     (Brain-field dropdown + operator + value + remove + "Add Rule") so
 *     the muscle memory is identical.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { LegNode, BrainField, CsvFieldChoice, CsvFilterRule, CsvDownloadFile, CsvDownloadSummary } from '../../types';
import * as api from '../../services/api';
import { SYSTEM_FIELDS, isSystemFieldId } from './systemFields';
import ClientAssignmentField from '../shared/ClientAssignmentField';

// Local alias for the summary endpoint's full response (type + guardrail
// limits the server includes alongside the breakdown). Lives here rather
// than in api.ts because the server-side limits aren't part of the shared
// frontend/backend type — they're only relevant to this modal's UI.
type FullSummary = CsvDownloadSummary & { maxRows: number; maxFileBytes: number; softWarnRows: number };

// -------------------------------------------------------------
// Shared little widgets (FieldTooltip, BrainFieldSelect) — kept
// local so this modal is self-contained.
// -------------------------------------------------------------

// Viewport-aware tooltip. Renders the bubble via a portal at document.body
// so it escapes any ancestor with overflow:hidden (the modal shell) and
// clamps horizontally so it never overflows the viewport on either edge.
// The `align` prop is kept for backward compat but is no longer needed —
// positioning is automatic. Defaults to "above the icon" and flips below
// if there isn't room (near the top of the viewport).
function FieldTooltip({ text }: { text: string; align?: 'center' | 'right' }) {
  const [coords, setCoords] = useState<{ left: number; top: number; placeBelow: boolean } | null>(null);
  const iconRef = React.useRef<HTMLSpanElement>(null);

  const TOOLTIP_WIDTH = 280;
  const EDGE_MARGIN = 8;
  const TOOLTIP_EST_HEIGHT = 80;

  const handleEnter = () => {
    if (!iconRef.current) {return;}
    const rect = iconRef.current.getBoundingClientRect();
    const vw = window.innerWidth;
    // Center on icon, then clamp to viewport so neither edge overflows.
    let left = rect.left + rect.width / 2 - TOOLTIP_WIDTH / 2;
    left = Math.max(EDGE_MARGIN, Math.min(left, vw - TOOLTIP_WIDTH - EDGE_MARGIN));
    // Place above by default; flip below if there's no room above.
    const placeBelow = rect.top < TOOLTIP_EST_HEIGHT + EDGE_MARGIN;
    const top = placeBelow ? rect.bottom + 6 : rect.top - 6;
    setCoords({ left, top, placeBelow });
  };

  return (
    <span
      ref={iconRef}
      style={{ position: 'relative', display: 'inline-block', marginLeft: 6, color: '#9ca3af', cursor: 'help', fontWeight: 'normal' }}
      onMouseEnter={handleEnter}
      onMouseLeave={() => setCoords(null)}
    >
      &#9432;
      {coords && createPortal(
        <span
          style={{
            position: 'fixed',
            left: coords.left,
            top: coords.top,
            transform: coords.placeBelow ? 'none' : 'translateY(-100%)',
            width: TOOLTIP_WIDTH,
            padding: '8px 10px',
            background: '#1f2937',
            color: '#f3f4f6',
            borderRadius: 6,
            fontSize: 12,
            lineHeight: 1.45,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            zIndex: 10000,
            fontWeight: 'normal',
            whiteSpace: 'normal',
            textAlign: 'left',
            pointerEvents: 'none',
          }}
        >
          {text}
        </span>,
        document.body
      )}
    </span>
  );
}

function BrainFieldDropdown({
  value,
  onChange,
  brainFields,
  placeholder,
  required,
  style,
}: {
  value: string;
  onChange: (v: string) => void;
  brainFields: BrainField[];
  placeholder?: string;
  required?: boolean;
  style?: React.CSSProperties;
}) {
  const brainNames = brainFields.map(f => f.fieldName);
  // System fields (system.import_date, ...) are always valid selections even
  // though they aren't in the Brain — don't flag them as stale.
  const isStale = !!value && !brainNames.includes(value) && !isSystemFieldId(value);
  const isMissing = !!required && !value;
  return (
    <div style={style}>
      <select
        className="form-input"
        value={value || ''}
        onChange={e => onChange(e.target.value)}
        style={isStale || isMissing ? { borderColor: '#ef4444' } : undefined}
      >
        <option value="">
          {brainFields.length === 0 ? '— Pick a field —' : (placeholder || '— Pick a Brain field —')}
        </option>
        {isStale && <option value={value}>{value} (not in Brain)</option>}
        {brainNames.map(n => (<option key={n} value={n}>{n}</option>))}
        <optgroup label="System Fields">
          {SYSTEM_FIELDS.map(s => (<option key={s.id} value={s.id} title={`e.g. ${s.example}`}>{s.label} — {s.example}</option>))}
        </optgroup>
      </select>
      {isStale && (
        <div style={{ color: '#92400e', fontSize: 12, marginTop: 4 }}>
          &#9888; "{value}" isn't in this Brain — it would silently match nothing.
        </div>
      )}
      {isMissing && (
        <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>
          Required. Pick a field from the Brain.
        </div>
      )}
    </div>
  );
}

function formatBytes(bytes: number | null | undefined): string {
  if (!bytes || bytes <= 0) {return '—';}
  const units = ['B', 'KB', 'MB', 'GB'];
  let v = bytes;
  let u = 0;
  while (v >= 1024 && u < units.length - 1) { v /= 1024; u++; }
  return `${v.toFixed(v < 10 && u > 0 ? 1 : 0)} ${units[u]}`;
}

function formatTs(iso: string | null | undefined): string {
  if (!iso) {return '—';}
  const d = new Date(iso);
  return d.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

// -------------------------------------------------------------
// Props
// -------------------------------------------------------------

interface Props {
  legNode: LegNode;
  onSave: (config: Record<string, any>) => void;
  onClose: () => void;
  /** Called after the Client field writes an assignment. */
  onClientAssigned?: () => void;
}

type View = 'config' | 'summary' | 'history';

// Internal editable filter rule shape — give each row a stable id so React
// doesn't blow away focus on every keystroke when a row's order shifts.
type EditableFilter = { id: string; field: string; operator: string; value: string };
function newRowId(): string {
  return Math.random().toString(36).slice(2) + Date.now().toString(36);
}

function CsvDownloadConfigModal({ legNode, onSave, onClose, onClientAssigned }: Props) {

  // -----------------------------------------------------------
  // Config (the part that's saved on the leg-node)
  // -----------------------------------------------------------
  const initialCsv = (legNode.config?.csv as any) || {};
  // Held as a string so the box can be genuinely blank (required field).
  // Defaulting to a number forced a "0" in that pinned the cursor and
  // wouldn't let you type a number cleanly.
  const [totalDesired, setTotalDesired] = useState<string>(
    typeof initialCsv.totalDesired === 'number' && initialCsv.totalDesired > 0
      ? String(initialCsv.totalDesired)
      : ''
  );
  const parsedTotalDesired = parseInt(totalDesired, 10);
  const totalDesiredInvalid = !Number.isFinite(parsedTotalDesired) || parsedTotalDesired <= 0;
  const [dateRangeFrom, setDateRangeFrom] = useState<string>(initialCsv.dateRangeFrom || '');
  const [dateRangeTo, setDateRangeTo] = useState<string>(initialCsv.dateRangeTo || '');
  const [fields, setFields] = useState<CsvFieldChoice[]>(
    Array.isArray(initialCsv.fields) ? initialCsv.fields : []
  );
  const [filters, setFilters] = useState<EditableFilter[]>(
    Array.isArray(initialCsv.filterRules)
      ? initialCsv.filterRules.map((r: any) => ({
          id: newRowId(),
          field: r.field || '',
          operator: r.operator || 'equals',
          value: r.value == null ? '' : String(r.value),
        }))
      : []
  );
  const [unique, setUnique] = useState<boolean>(!!initialCsv.unique);

  // -----------------------------------------------------------
  // Brain fields + per-instance stats + file history
  // -----------------------------------------------------------
  const [brainFields, setBrainFields] = useState<BrainField[]>([]);
  const [stats, setStats] = useState<api.CsvDownloadStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(true);
  const [files, setFiles] = useState<CsvDownloadFile[]>([]);
  const [filesLoading, setFilesLoading] = useState(true);

  // -----------------------------------------------------------
  // View / async state
  // -----------------------------------------------------------
  const [view, setView] = useState<View>('config');
  const [summary, setSummary] = useState<FullSummary | null>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [summaryError, setSummaryError] = useState<string | null>(null);
  const [finalizing, setFinalizing] = useState(false);
  const [activeFileId, setActiveFileId] = useState<string | null>(null);

  // -----------------------------------------------------------
  // Refresh stats + file history. Declared BEFORE the initial-load
  // useEffect so it's in scope when that effect runs (TS const declarations
  // are block-scoped, not hoisted, so the order matters).
  // -----------------------------------------------------------
  const refreshStatsAndFiles = useCallback(() => {
    setStatsLoading(true);
    api.getCsvDownloadStats(legNode.dataBodyId, legNode.id)
      .then(s => { setStats(s); setStatsLoading(false); })
      .catch(err => {
        console.error('Failed to load CSV download stats:', err);
        setStats({ filesCount: 0, rowsTotal: 0, lastDownloadAt: null, uniqueLeadsTracked: 0 });
        setStatsLoading(false);
      });
    setFilesLoading(true);
    api.getCsvDownloadFiles(legNode.dataBodyId, legNode.id)
      .then(f => { setFiles(f); setFilesLoading(false); })
      .catch(err => {
        console.error('Failed to load CSV download files:', err);
        setFiles([]);
        setFilesLoading(false);
      });
  }, [legNode.dataBodyId, legNode.id]);

  // -----------------------------------------------------------
  // Initial load
  // -----------------------------------------------------------
  useEffect(() => {
    api.getBrainFields(legNode.dataBodyId).then(setBrainFields).catch(err => {
      console.error('Failed to load brain fields:', err);
    });
    refreshStatsAndFiles();
  }, [legNode.id, legNode.dataBodyId, refreshStatsAndFiles]);

  // -----------------------------------------------------------
  // Polling: while we have a job in queued/processing, poll /files
  // every 2s so the progress bar updates and the user sees the row
  // flip to 'completed' without manual refresh.
  // -----------------------------------------------------------
  useEffect(() => {
    if (!activeFileId) {return;}
    const interval = setInterval(async () => {
      try {
        const file = await api.getCsvDownloadFile(legNode.dataBodyId, legNode.id, activeFileId);
        setFiles(prev => {
          const i = prev.findIndex(f => f.id === file.id);
          if (i === -1) {return [file, ...prev];}
          const copy = prev.slice();
          copy[i] = file;
          return copy;
        });
        if (file.status === 'completed' || file.status === 'failed') {
          setActiveFileId(null);
          // Stats include row totals, refresh them
          api.getCsvDownloadStats(legNode.dataBodyId, legNode.id).then(setStats).catch(() => {});
        }
      } catch (err) {
        console.warn('Polling /files failed:', err);
      }
    }, 2000);
    return () => clearInterval(interval);
  }, [activeFileId, legNode.dataBodyId, legNode.id]);

  // -----------------------------------------------------------
  // Field-list helpers
  // -----------------------------------------------------------
  const fieldNames = fields.map(f => f.name);
  const availableBrainFields = brainFields.filter(b => !fieldNames.includes(b.fieldName));

  function addField(name: string) {
    if (!name) {return;}
    if (fieldNames.includes(name)) {return;}
    setFields(prev => [...prev, { name, required: false }]);
  }
  function removeField(name: string) {
    setFields(prev => prev.filter(f => f.name !== name));
  }
  function selectAllFields() {
    // Add every Brain field not already selected, preserving existing
    // required flags and order. New fields default to optional.
    setFields(prev => {
      const have = new Set(prev.map(f => f.name));
      const additions = brainFields
        .filter(b => !have.has(b.fieldName))
        .map(b => ({ name: b.fieldName, required: false }));
      return [...prev, ...additions];
    });
  }
  function toggleFieldRequired(name: string) {
    setFields(prev => prev.map(f => f.name === name ? { ...f, required: !f.required } : f));
  }

  // -----------------------------------------------------------
  // Filter helpers — mirrors vertebra filter_rule builder
  // -----------------------------------------------------------
  function addFilter() {
    setFilters(prev => [...prev, { id: newRowId(), field: '', operator: 'equals', value: '' }]);
  }
  function updateFilter(id: string, patch: Partial<EditableFilter>) {
    setFilters(prev => prev.map(f => f.id === id ? { ...f, ...patch } : f));
  }
  function removeFilter(id: string) {
    setFilters(prev => prev.filter(f => f.id !== id));
  }

  // -----------------------------------------------------------
  // Compose the config payload sent to backend / saved on the leg-node
  // -----------------------------------------------------------
  function buildConfig(): api.CsvDownloadConfigInput {
    return {
      totalDesired: !totalDesiredInvalid ? parsedTotalDesired : undefined,
      dateRangeFrom: dateRangeFrom || null,
      dateRangeTo: dateRangeTo || null,
      fields: fields.filter(f => !!f.name),
      filterRules: filters
        .filter(f => f.field && f.operator)
        .map(f => ({
          field: f.field,
          operator: f.operator as CsvFilterRule['operator'],
          value: f.value,
        })),
      unique,
    };
  }

  // -----------------------------------------------------------
  // Save (persists config onto leg_nodes.config.csv)
  // -----------------------------------------------------------
  function handleSave() {
    if (fields.length === 0) {
      alert('Pick at least one field to include in the CSV.');
      return;
    }
    const cfg = buildConfig();
    onSave({
      ...legNode.config,
      csv: cfg,
    });
  }

  // -----------------------------------------------------------
  // Download flow — Summary
  // -----------------------------------------------------------
  async function runSummary() {
    if (fields.length === 0) {
      alert('Pick at least one field to include in the CSV.');
      return;
    }
    if (totalDesiredInvalid) {
      alert('Enter how many leads you want in Total Desired.');
      return;
    }
    setSummaryError(null);
    setSummaryLoading(true);
    setView('summary');
    try {
      const s = await api.getCsvDownloadSummary(legNode.dataBodyId, legNode.id, buildConfig());
      setSummary(s);
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to compute summary';
      setSummaryError(msg);
    } finally {
      setSummaryLoading(false);
    }
  }

  // -----------------------------------------------------------
  // Download flow — Finalize (enqueue export, switch to history view)
  // -----------------------------------------------------------
  async function runFinalize() {
    if (!summary) {return;}
    setFinalizing(true);
    try {
      const { fileId } = await api.finalizeCsvDownload(
        legNode.dataBodyId, legNode.id, buildConfig(), summary
      );
      setActiveFileId(fileId);
      setView('history');
      // Optimistically add a placeholder row so the user sees the file
      // immediately; the polling effect will replace it with real data.
      setFiles(prev => ([
        {
          id: fileId,
          legNodeId: legNode.id,
          dataBodyId: legNode.dataBodyId,
          filename: 'preparing…',
          rowCount: 0,
          fileSizeBytes: null,
          status: 'queued',
          progressPct: 0,
          error: null,
          createdAt: new Date().toISOString(),
          startedAt: null,
          completedAt: null,
          expiresAt: null,
        },
        ...prev,
      ]));
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to enqueue export';
      alert(`Could not start export: ${msg}`);
    } finally {
      setFinalizing(false);
    }
  }

  async function handleDownloadClick(file: CsvDownloadFile) {
    if (file.status !== 'completed') {return;}
    try {
      const { url } = await api.mintCsvDownloadUrl(legNode.dataBodyId, legNode.id, file.id);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || 'Could not mint download URL';
      alert(msg);
    }
  }

  async function handleDeleteFile(file: CsvDownloadFile) {
    if (!window.confirm(`Delete "${file.filename}"? This also forgets the ${file.rowCount.toLocaleString()} lead IDs this file remembered, so they may re-export with the "unique" filter on.`)) {
      return;
    }
    try {
      await api.deleteCsvDownloadFile(legNode.dataBodyId, legNode.id, file.id);
      setFiles(prev => prev.filter(f => f.id !== file.id));
      refreshStatsAndFiles();
    } catch (err: any) {
      alert(`Could not delete file: ${err?.response?.data?.error || err?.message || 'unknown error'}`);
    }
  }

  // -----------------------------------------------------------
  // Render — modal frame with view-switched body
  // -----------------------------------------------------------
  return (
    <div className="modal-overlay" onClick={onClose}>
      <div
        className="modal"
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: view === 'history' ? 860 : view === 'summary' ? 720 : 640,
          maxHeight: '90vh',
          overflowY: 'auto',
        }}
      >
        {/* ---------------------------------------------------- */}
        {/* Header — title + type + top-right downloaded counter */}
        {/* ---------------------------------------------------- */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 4 }}>
          <h2 className="modal-title" style={{ margin: 0 }}>Configure: {legNode.name}</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: 'flex-end', textAlign: 'right', whiteSpace: 'nowrap', marginTop: 4 }}>
            <div style={{ display: 'flex', gap: 12, fontSize: 11, color: '#6b7280' }}>
              {statsLoading ? (
                <>
                  <span style={{ color: '#2563eb', fontSize: 11, fontWeight: 500 }}>
                    Downloaded: <span className="stat-skeleton" style={{ width: 40, height: 11, verticalAlign: 'middle', display: 'inline-block' }} />
                  </span>
                  <span style={{ color: '#6b7280', fontSize: 11, fontWeight: 500 }}>
                    Files: <span className="stat-skeleton" style={{ width: 24, height: 11, verticalAlign: 'middle', display: 'inline-block' }} />
                  </span>
                </>
              ) : stats ? (
                <>
                  <span style={{ color: '#2563eb', fontSize: 11, fontWeight: 500 }}>
                    Downloaded: {stats.rowsTotal.toLocaleString()} {stats.rowsTotal === 1 ? 'lead' : 'leads'}
                  </span>
                  <span style={{ color: '#6b7280', fontSize: 11, fontWeight: 500 }}>
                    Files: {stats.filesCount.toLocaleString()}
                  </span>
                </>
              ) : null}
            </div>
            {!statsLoading && stats?.lastDownloadAt && (
              <div style={{ fontSize: 11, color: '#9ca3af' }}>
                Last: {formatTs(stats.lastDownloadAt)}
              </div>
            )}
          </div>
        </div>
        <div style={{ fontSize: 13, color: '#9ca3af', marginBottom: 16 }}>
          Type: .csv file download
          <FieldTooltip
            text="This leg type does NOT receive incoming leads. The Brain still collects leads as normal — when you click Download below, this leg pulls a CSV of matching leads on demand. Use the unique toggle (after the first file) to avoid pulling the same lead twice from this instance."
          />
        </div>

        {/* Client — deliberately the first thing in the modal. Who a
            destination delivers to is the only decision in here that
            cannot be undone once a lead has gone out. Self-contained:
            it fetches and writes the assignment itself rather than
            riding on this modal's own save flow. */}
        <ClientAssignmentField
          dataBodyId={legNode.dataBodyId}
          legNodeId={legNode.id}
          nodeType={legNode.type}
          onAssigned={onClientAssigned}
        />

        {/* ---------------------------------------------------- */}
        {/* View tab switcher                                    */}
        {/* ---------------------------------------------------- */}
        <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid #e5e7eb', marginBottom: 16 }}>
          {[
            { id: 'config' as View, label: 'Config' },
            { id: 'history' as View, label: `History${files.length > 0 ? ` (${files.length})` : ''}` },
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setView(tab.id)}
              style={{
                background: 'none',
                border: 'none',
                padding: '8px 14px',
                cursor: 'pointer',
                fontSize: 13,
                fontWeight: 500,
                color: view === tab.id ? '#2563eb' : '#6b7280',
                borderBottom: view === tab.id ? '2px solid #2563eb' : '2px solid transparent',
                marginBottom: -1,
              }}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ====================================================== */}
        {/* CONFIG VIEW                                            */}
        {/* ====================================================== */}
        {view === 'config' && (
          <>
            {/* Total desired */}
            <div className="form-group">
              <label className="form-label">
                Total Desired
                <FieldTooltip text="The maximum number of leads to put in the CSV. Hard-capped server-side at 1,000,000 rows / 500MB per file to keep browsers from crashing. If your filtered match-count is higher than this number, the export takes the oldest leads first." />
              </label>
              <input
                type="number"
                className="form-input"
                min={1}
                step={1}
                placeholder="e.g. 10000"
                value={totalDesired}
                onChange={e => setTotalDesired(e.target.value)}
                style={totalDesiredInvalid
                  ? { borderColor: '#ef4444', boxShadow: '0 0 0 3px rgba(239,68,68,0.18)' }
                  : undefined}
              />
              {totalDesiredInvalid && (
                <div style={{ color: '#ef4444', fontSize: 12, marginTop: 4 }}>
                  Required. Enter how many leads you want.
                </div>
              )}
            </div>

            {/* Date range */}
            <div className="form-group">
              <label className="form-label">
                Lead Date
                <FieldTooltip text="Filter by when the lead first hit the Brain (leads.received_at). Dates are read in your account's time zone (set in Organization Settings), not UTC. Leave both blank to include leads from any time. The 'To' bound is exclusive — e.g. To = 2026-05-23 means everything before midnight on the 23rd in your account's local time." />
              </label>
              <div className="form-row">
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>From</div>
                  <input
                    type="date"
                    className="form-input"
                    value={dateRangeFrom}
                    onChange={e => setDateRangeFrom(e.target.value)}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontSize: 11, color: '#6b7280', marginBottom: 4 }}>To (exclusive)</div>
                  <input
                    type="date"
                    className="form-input"
                    value={dateRangeTo}
                    onChange={e => setDateRangeTo(e.target.value)}
                  />
                </div>
              </div>
            </div>

            {/* Fields */}
            <div className="form-group">
              <label className="form-label" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <span>
                  Fields
                  <FieldTooltip
                    align="right"
                    text="Which Brain fields to include as columns in the CSV. Mark a field 'Required' to drop leads that are missing it (same strictness model as required Brain fields). Pick from the dropdown to add; click the orange dot to toggle required; × removes."
                  />
                </span>
                {availableBrainFields.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={selectAllFields}
                    title="Add every Brain field as a column"
                    style={{ fontWeight: 500 }}
                  >
                    Select All
                  </button>
                )}
              </label>
              <div style={{ background: '#f8f9fb', border: '1px solid #e5e7eb', borderRadius: 6, padding: 10 }}>
                {fields.length === 0 && (
                  <div style={{ fontSize: 12, color: '#9ca3af', padding: '8px 0', textAlign: 'center' }}>
                    No fields selected — pick at least one to enable Download.
                  </div>
                )}
                {fields.map(f => (
                  <div
                    key={f.name}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '6px 8px',
                      marginBottom: 4,
                      background: '#fff',
                      border: '1px solid #e5e7eb',
                      borderRadius: 6,
                    }}
                  >
                    <span style={{ flex: 1, fontWeight: 500, fontSize: 13 }}>{f.name}</span>
                    <button
                      onClick={() => toggleFieldRequired(f.name)}
                      title={f.required ? 'Required — leads without this field are dropped' : 'Optional — empty values become blank cells'}
                      style={{
                        background: f.required ? '#fee2e2' : '#f3f4f6',
                        color: f.required ? '#b91c1c' : '#6b7280',
                        border: f.required ? '1px solid #fca5a5' : '1px solid #d1d5db',
                        borderRadius: 4,
                        fontSize: 11,
                        fontWeight: 600,
                        padding: '2px 8px',
                        cursor: 'pointer',
                      }}
                    >
                      {f.required ? 'REQUIRED' : 'optional'}
                    </button>
                    <button
                      onClick={() => removeField(f.name)}
                      className="btn-icon"
                      style={{ color: '#ef4444', width: 24, height: 24 }}
                      title="Remove field"
                    >
                      ×
                    </button>
                  </div>
                ))}
                {availableBrainFields.length > 0 ? (
                  <select
                    className="form-input"
                    value=""
                    onChange={e => { addField(e.target.value); e.target.value = ''; }}
                    style={{ marginTop: 6 }}
                  >
                    <option value="" disabled>+ Add a field from the Brain…</option>
                    {availableBrainFields.map(b => (
                      <option key={b.fieldName} value={b.fieldName}>{b.fieldName}</option>
                    ))}
                  </select>
                ) : brainFields.length > 0 ? (
                  <div style={{ fontSize: 12, color: '#6b7280', padding: '6px 0', textAlign: 'center' }}>
                    All Brain fields are already selected.
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: '#6b7280', padding: '6px 0', textAlign: 'center' }}>
                    No Brain fields yet — configure the Brain first.
                  </div>
                )}
              </div>
            </div>

            {/* Filter By */}
            <div className="form-group">
              <label className="form-label">
                Filter By
                <FieldTooltip
                  align="right"
                  text="Filter rules — same shape as the Vertebrae filter rule. Pick a Brain field, an operator, and a value. Add more rules with the button below — all rules must pass for a lead to be included (AND). Example: state equals 'CA' AND email contains '@'."
                />
              </label>
              {filters.length === 0 && (
                <div style={{ fontSize: 12, color: '#9ca3af', padding: '8px 0', textAlign: 'center', background: '#f8f9fb', border: '1px dashed #e5e7eb', borderRadius: 6 }}>
                  No filters — every lead in the date range will be included.
                </div>
              )}
              {filters.map(rule => (
                <div key={rule.id} className="form-row" style={{ marginBottom: 8 }}>
                  <BrainFieldDropdown
                    value={rule.field}
                    onChange={v => updateFilter(rule.id, { field: v })}
                    brainFields={brainFields}
                    required
                    placeholder="— Pick a Brain field —"
                    style={{ flex: 1 }}
                  />
                  <select
                    className="form-select"
                    value={rule.operator}
                    onChange={e => updateFilter(rule.id, { operator: e.target.value })}
                    style={{ width: 130 }}
                  >
                    <option value="equals">Equals</option>
                    <option value="not_equals">Not Equals</option>
                    <option value="contains">Contains</option>
                    <option value="not_contains">Not Contains</option>
                    <option value="greater_than">Greater Than</option>
                    <option value="less_than">Less Than</option>
                    <option value="exists">Exists</option>
                    <option value="not_exists">Not Exists</option>
                    <option value="regex">Regex</option>
                  </select>
                  <input
                    className="form-input"
                    placeholder="Value"
                    value={rule.value}
                    onChange={e => updateFilter(rule.id, { value: e.target.value })}
                    style={{ flex: 1 }}
                  />
                  <button
                    className="btn-icon"
                    onClick={() => removeFilter(rule.id)}
                    style={{ color: '#ef4444' }}
                    title="Remove filter"
                  >×</button>
                </div>
              ))}
              <button className="btn btn-secondary btn-sm" onClick={addFilter} style={{ marginTop: 6 }}>
                + Add Filter
              </button>
            </div>

            {/* Unique toggle — visible only after at least one file exists */}
            {(stats && stats.filesCount > 0) ? (
              <div className="form-group">
                <label
                  className="form-label"
                  style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
                >
                  <input
                    type="checkbox"
                    checked={unique}
                    onChange={e => setUnique(e.target.checked)}
                  />
                  Unique
                  <FieldTooltip
                    align="right"
                    text="When ON, only export leads that this instance hasn't downloaded before. Each .csv-download instance has its own memory — pulling 'unique' leads from one instance has no effect on the others. Toggle OFF to allow re-exporting leads (e.g. you sent the last batch to a vendor and need a fresh copy of the same leads)."
                  />
                </label>
                {unique && (
                  <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4, paddingLeft: 24 }}>
                    Excluding {stats.uniqueLeadsTracked.toLocaleString()} {stats.uniqueLeadsTracked === 1 ? 'lead' : 'leads'} already exported by this instance.
                  </div>
                )}
              </div>
            ) : null}

            {/* Action buttons */}
            <div className="modal-actions" style={{ justifyContent: 'space-between' }}>
              <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn btn-secondary" onClick={handleSave}>
                  Save Configuration
                </button>
                <button
                  className="btn btn-primary"
                  onClick={runSummary}
                  disabled={fields.length === 0 || totalDesiredInvalid}
                  title={fields.length === 0
                    ? 'Pick at least one field to enable Download'
                    : totalDesiredInvalid
                      ? 'Enter a Total Desired to enable Download'
                      : 'Build a summary report of what would be downloaded'}
                >
                  Download…
                </button>
              </div>
            </div>
          </>
        )}

        {/* ====================================================== */}
        {/* SUMMARY VIEW                                           */}
        {/* ====================================================== */}
        {view === 'summary' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <button onClick={() => setView('config')} className="btn btn-secondary btn-sm">
                ← Back to config
              </button>
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                Summary of what would download
                <FieldTooltip
                  align="right"
                  text="Read this carefully BEFORE finalizing. The numbers below are what the worker will actually export — including how many leads got dropped by required-field strictness or each filter rule. If something looks wrong, go back and adjust."
                />
              </div>
            </div>

            {summaryLoading && (
              <div style={{ padding: 60, textAlign: 'center', color: '#6b7280' }}>
                <div className="stat-skeleton" style={{ width: 220, height: 16, margin: '0 auto 8px' }} />
                <div className="stat-skeleton" style={{ width: 180, height: 12, margin: '0 auto' }} />
                <div style={{ fontSize: 12, marginTop: 16 }}>Counting leads against your filters…</div>
              </div>
            )}

            {!summaryLoading && summaryError && (
              <div style={{
                padding: 14,
                background: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: 6,
                color: '#991b1b',
                fontSize: 13,
                marginBottom: 16,
              }}>
                <strong>Couldn't compute summary:</strong> {summaryError}
              </div>
            )}

            {!summaryLoading && summary && (
              <>
                {/* Big eligible count */}
                <div style={{
                  background: '#eff6ff',
                  border: '1px solid #bfdbfe',
                  borderRadius: 8,
                  padding: 20,
                  textAlign: 'center',
                  marginBottom: 16,
                }}>
                  <div style={{ fontSize: 12, color: '#1e40af', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 4 }}>
                    Will export
                  </div>
                  <div style={{ fontSize: 36, fontWeight: 700, color: '#1e3a8a' }}>
                    {summary.eligible.toLocaleString()}
                  </div>
                  <div style={{ fontSize: 13, color: '#1e40af', marginTop: 4 }}>
                    {summary.eligible === 1 ? 'lead' : 'leads'}
                  </div>
                  {summary.cappedAt != null && (
                    <div style={{ marginTop: 8, padding: '6px 10px', background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, fontSize: 12, color: '#92400e', display: 'inline-block' }}>
                      ⚠ Capped at {summary.cappedAt.toLocaleString()} (your hard limit). Narrow the date range or filters to get the rest in a second file.
                    </div>
                  )}
                  {summary.eligible > summary.softWarnRows && summary.eligible <= summary.maxRows && (
                    <div style={{ marginTop: 8, padding: '6px 10px', background: '#fefce8', border: '1px solid #fde047', borderRadius: 6, fontSize: 12, color: '#854d0e', display: 'inline-block' }}>
                      Large file warning — {summary.eligible.toLocaleString()} rows may be slow to open in Excel. Consider narrowing the filters.
                    </div>
                  )}
                </div>

                {/* Breakdown */}
                <div style={{ background: '#f8f9fb', border: '1px solid #e5e7eb', borderRadius: 6, padding: 14, marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 8 }}>
                    Why these numbers?
                  </div>
                  <div style={{ fontSize: 13, lineHeight: 1.7 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                      <span>Leads in date range:</span>
                      <strong>{summary.totalInDateRange.toLocaleString()}</strong>
                    </div>
                    {summary.missingRequiredField > 0 && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#92400e' }}>
                        <span>− Missing a required field:</span>
                        <strong>{summary.missingRequiredField.toLocaleString()}</strong>
                      </div>
                    )}
                    {summary.byFilter.map((b: { rule: CsvFilterRule; rejected: number }, i: number) => b.rejected > 0 && (
                      <div key={i} style={{ display: 'flex', justifyContent: 'space-between', color: '#92400e' }}>
                        <span>− Filtered out ({b.rule.field} {b.rule.operator} "{b.rule.value}"):</span>
                        <strong>{b.rejected.toLocaleString()}</strong>
                      </div>
                    ))}
                    {summary.alreadyDownloaded > 0 && unique && (
                      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6366f1' }}>
                        <span>− Already downloaded by this instance:</span>
                        <strong>{summary.alreadyDownloaded.toLocaleString()}</strong>
                      </div>
                    )}
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, paddingTop: 8, borderTop: '1px dashed #d1d5db' }}>
                      <strong>= Eligible to export:</strong>
                      <strong style={{ color: '#1e3a8a' }}>{summary.eligible.toLocaleString()}</strong>
                    </div>
                  </div>
                </div>

                {/* Final confirm */}
                <div className="modal-actions" style={{ justifyContent: 'space-between' }}>
                  <button className="btn btn-secondary" onClick={() => setView('config')}>
                    Back
                  </button>
                  <button
                    className="btn btn-primary"
                    onClick={runFinalize}
                    disabled={finalizing || summary.eligible === 0}
                    title={summary.eligible === 0 ? 'No leads match these filters' : 'Start the export — file will appear in History'}
                  >
                    {finalizing ? 'Starting…' : `Finalize & Download ${summary.eligible.toLocaleString()} leads`}
                  </button>
                </div>
              </>
            )}
          </>
        )}

        {/* ====================================================== */}
        {/* HISTORY VIEW                                           */}
        {/* ====================================================== */}
        {view === 'history' && (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontSize: 13, color: '#6b7280' }}>
                Every CSV ever pulled from THIS instance. File is available for re-download for up to 30 days.
                <FieldTooltip
                  align="right"
                  text="History is scoped to this specific .csv-download instance. Files from other instances on this leg (or other legs) live in their own histories. Deleting a row from this list also forgets the lead IDs it remembered (those leads may re-export with 'unique' on)."
                />
              </div>
              <button
                onClick={refreshStatsAndFiles}
                className="btn btn-secondary btn-sm"
                title="Reload from server"
              >
                ↻ Refresh
              </button>
            </div>

            {filesLoading ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af' }}>Loading…</div>
            ) : files.length === 0 ? (
              <div style={{ padding: 40, textAlign: 'center', color: '#9ca3af', background: '#f8f9fb', border: '1px dashed #e5e7eb', borderRadius: 8 }}>
                No downloads yet. Configure filters in the Config tab and click Download.
              </div>
            ) : (
              <div style={{ border: '1px solid #e5e7eb', borderRadius: 8, overflow: 'hidden' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                  <thead>
                    <tr style={{ background: '#f8f9fb', color: '#6b7280' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Created</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>File</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Rows</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Size</th>
                      <th style={{ padding: '8px 12px', textAlign: 'center', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}>Status</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.04em' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {files.map((f, i) => (
                      <tr key={f.id} style={{ borderTop: '1px solid #f3f4f6', background: i % 2 === 0 ? '#fff' : '#fafbfc' }}>
                        <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#6b7280' }}>{formatTs(f.createdAt)}</td>
                        <td style={{ padding: '10px 12px', color: '#111827', fontFamily: 'monospace', fontSize: 12, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={f.filename}>{f.filename}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: '#111827' }}>{f.rowCount.toLocaleString()}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'right', color: '#6b7280' }}>{formatBytes(f.fileSizeBytes)}</td>
                        <td style={{ padding: '10px 12px', textAlign: 'center' }}>
                          {f.status === 'queued' && (
                            <span style={{ color: '#6b7280', fontSize: 12 }}>Queued…</span>
                          )}
                          {f.status === 'processing' && (
                            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                              <div style={{ width: 80, height: 6, background: '#e5e7eb', borderRadius: 3, overflow: 'hidden' }}>
                                <div style={{ width: `${f.progressPct}%`, height: '100%', background: '#6366f1', transition: 'width 0.3s' }} />
                              </div>
                              <span style={{ fontSize: 11, color: '#6366f1' }}>{f.progressPct}%</span>
                            </div>
                          )}
                          {f.status === 'completed' && (
                            <span style={{ color: '#059669', fontSize: 12, fontWeight: 600 }}>✓ Ready</span>
                          )}
                          {f.status === 'failed' && (
                            <span title={f.error || ''} style={{ color: '#dc2626', fontSize: 12, fontWeight: 600 }}>✗ Failed</span>
                          )}
                          {f.status === 'expired' && (
                            <span style={{ color: '#9ca3af', fontSize: 12 }}>Expired</span>
                          )}
                        </td>
                        <td style={{ padding: '10px 12px', textAlign: 'right' }}>
                          {f.status === 'completed' && (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleDownloadClick(f)}
                              style={{ marginRight: 4 }}
                            >
                              ⬇ Download
                            </button>
                          )}
                          <button
                            className="btn-icon"
                            onClick={() => handleDeleteFile(f)}
                            title="Delete file (also forgets its lead-ID memory)"
                            style={{ color: '#ef4444' }}
                          >
                            🗑
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="modal-actions" style={{ marginTop: 16, justifyContent: 'space-between' }}>
              <button className="btn btn-secondary" onClick={() => setView('config')}>
                ← Back to config
              </button>
              <button className="btn btn-secondary" onClick={onClose}>Close</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export default CsvDownloadConfigModal;
